/**
 * /cuenta — Perfil personal del user.
 *
 * Edita nombre, foto (avatar), teléfono. Email se cambia con flow
 * separado (start + verify OTP) accesible desde la card de Email.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Mail, Phone, Save, User } from 'lucide-react'
import { toast } from 'sonner'

import { meApi, type MyProfile } from '@/lib/api'
import { AccountLayout } from './layout'

export function AccountProfilePage() {
  const qc = useQueryClient()
  const profile = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => meApi.profile(),
  })

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCC, setPhoneCC] = useState('+57')
  const [phonePassword, setPhonePassword] = useState('')
  const [phoneTotp, setPhoneTotp] = useState('')
  const [phoneEditing, setPhoneEditing] = useState(false)

  useEffect(() => {
    if (profile.data) {
      setFirstName(profile.data.first_name ?? '')
      setLastName(profile.data.last_name ?? '')
      setPhone(profile.data.phone ?? '')
      setPhoneCC(profile.data.phone_country_code ?? '+57')
    }
  }, [profile.data])

  const saveName = useMutation({
    mutationFn: () =>
      meApi.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      }),
    onSuccess: () => {
      toast.success('Nombre actualizado')
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
    },
    onError: (e: Error) => toast.error(`Error: ${e.message}`),
  })

  const savePhone = useMutation({
    mutationFn: () =>
      meApi.updatePhone({
        phone: phone.trim() || null,
        phone_country_code: phoneCC.trim() || null,
        password: phonePassword || undefined,
        totp_code: phoneTotp || undefined,
      }),
    onSuccess: () => {
      toast.success('Teléfono actualizado')
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
      setPhoneEditing(false)
      setPhonePassword('')
      setPhoneTotp('')
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      const msg = err.response?.data?.detail?.message ?? (e as Error).message
      toast.error(`Error: ${msg}`)
    },
  })

  if (profile.isLoading) {
    return (
      <AccountLayout>
        <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
      </AccountLayout>
    )
  }

  return (
    <AccountLayout>
      <div className="space-y-6">
        {/* Avatar + identidad */}
        <Section title="Identidad" icon={User}>
          <div className="flex items-start gap-4">
            <Avatar profile={profile.data} />
            <div className="flex-1 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nombre">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    maxLength={200}
                    className={inputCls}
                  />
                </Field>
                <Field label="Apellido">
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    maxLength={200}
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => saveName.mutate()}
                  disabled={
                    saveName.isPending ||
                    (firstName === (profile.data?.first_name ?? '') &&
                      lastName === (profile.data?.last_name ?? ''))
                  }
                  className={btnPrimaryCls}
                >
                  {saveName.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" /> Guardar nombre
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Section>

        {/* Email */}
        <EmailCard profile={profile.data} />

        {/* Teléfono */}
        <Section title="Teléfono" icon={Phone}>
          {!phoneEditing ? (
            <div className="flex items-center justify-between">
              <div>
                {profile.data?.phone ? (
                  <p className="font-mono text-sm">
                    {profile.data.phone_country_code} {profile.data.phone}
                  </p>
                ) : (
                  <p className="text-sm text-fg-muted">Sin teléfono registrado</p>
                )}
                {profile.data?.phone && (
                  <p className="mt-1 text-xs text-fg-muted">
                    {profile.data.phone_verified ? (
                      <span className="inline-flex items-center gap-1 text-success-fg">
                        <Check className="h-3 w-3" /> Verificado
                      </span>
                    ) : (
                      <span className="text-warning-fg">No verificado</span>
                    )}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setPhoneEditing(true)}
                className="text-xs text-brand-600 hover:underline"
              >
                {profile.data?.phone ? 'Cambiar' : 'Agregar teléfono'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-[100px_1fr]">
                <Field label="Cód. país">
                  <input
                    type="text"
                    value={phoneCC}
                    onChange={(e) => setPhoneCC(e.target.value)}
                    maxLength={5}
                    className={`${inputCls} font-mono`}
                    placeholder="+57"
                  />
                </Field>
                <Field label="Número">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={20}
                    className={`${inputCls} font-mono`}
                    placeholder="3001234567"
                  />
                </Field>
              </div>
              <Field
                label="Contraseña actual"
                help="Para evitar que alguien con tu sesión cambie tu contacto."
              >
                <input
                  type="password"
                  value={phonePassword}
                  onChange={(e) => setPhonePassword(e.target.value)}
                  className={inputCls}
                  autoComplete="current-password"
                />
              </Field>
              {profile.data?.totp_enabled && (
                <Field
                  label="Código TOTP"
                  help="Tu authenticator app — 6 dígitos."
                >
                  <input
                    type="text"
                    value={phoneTotp}
                    onChange={(e) =>
                      setPhoneTotp(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    inputMode="numeric"
                    maxLength={6}
                    className={`${inputCls} font-mono w-32`}
                    placeholder="123456"
                  />
                </Field>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPhoneEditing(false)
                    setPhone(profile.data?.phone ?? '')
                    setPhoneCC(profile.data?.phone_country_code ?? '+57')
                    setPhonePassword('')
                    setPhoneTotp('')
                  }}
                  className={btnSecondaryCls}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => savePhone.mutate()}
                  disabled={savePhone.isPending || !phonePassword}
                  className={btnPrimaryCls}
                >
                  {savePhone.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" /> Guardar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </Section>
      </div>
    </AccountLayout>
  )
}

function Avatar({ profile }: { profile?: MyProfile }) {
  if (profile?.avatar_url) {
    return (
      <img
        src={profile.avatar_url}
        alt={profile.full_name}
        className="h-20 w-20 rounded-full object-cover border border-border"
      />
    )
  }
  return (
    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-500/10 text-2xl font-semibold text-brand-600 border border-border">
      {profile?.initials ?? '?'}
    </div>
  )
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-bg-surface p-6">
      <header className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-brand-600" />
        <h2 className="text-lg font-semibold">{title}</h2>
      </header>
      <div className="mt-4">{children}</div>
    </section>
  )
}

function Field({
  label,
  help,
  children,
}: {
  label: string
  help?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wide text-fg-secondary">
        {label}
      </label>
      {children}
      {help && <p className="mt-1 text-xs text-fg-muted">{help}</p>}
    </div>
  )
}

// ─── EmailCard ───────────────────────────────────────────────────────
// Self-service email change — flow:
//   1. User clic "Cambiar email" → form pide new_email + password / TOTP.
//   2. POST /me/email/change/start → backend envía OTP al new_email.
//   3. Form pide código de verificación.
//   4. POST /me/email/change/verify → email actualizado.

function EmailCard({ profile }: { profile?: MyProfile }) {
  const qc = useQueryClient()
  const [stage, setStage] = useState<'idle' | 'request' | 'verify'>('idle')
  const [newEmail, setNewEmail] = useState('')
  const [maskedTarget, setMaskedTarget] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [verifyCode, setVerifyCode] = useState('')

  const startChange = useMutation({
    mutationFn: () =>
      meApi.emailChangeStart({
        new_email: newEmail.trim(),
        password: password || undefined,
        totp_code: totpCode || undefined,
      }),
    onSuccess: (data) => {
      setMaskedTarget(data.new_email_masked)
      setStage('verify')
      toast.success(`Código enviado a ${data.new_email_masked}`)
      // limpiar password para que no quede en estado.
      setPassword('')
      setTotpCode('')
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    },
  })

  const verifyChange = useMutation({
    mutationFn: () => meApi.emailChangeVerify(verifyCode.trim()),
    onSuccess: (data) => {
      toast.success(`Email actualizado a ${data.email}`)
      setStage('idle')
      setNewEmail('')
      setVerifyCode('')
      setMaskedTarget('')
      qc.invalidateQueries({ queryKey: ['me', 'profile'] })
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    },
  })

  function cancel() {
    setStage('idle')
    setNewEmail('')
    setPassword('')
    setTotpCode('')
    setVerifyCode('')
    setMaskedTarget('')
  }

  const canStart =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim()) &&
    (password.length > 0 || (profile?.totp_enabled && totpCode.length === 6)) &&
    !startChange.isPending

  return (
    <Section title="Email" icon={Mail}>
      {stage === 'idle' && (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-sm">{profile?.email}</p>
            <p className="mt-1 text-xs text-fg-muted">
              {profile?.email_verified ? (
                <span className="inline-flex items-center gap-1 text-success-fg">
                  <Check className="h-3 w-3" /> Verificado
                </span>
              ) : (
                <span className="text-warning-fg">No verificado</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setStage('request')}
            className="text-xs text-brand-600 hover:underline"
          >
            Cambiar email
          </button>
        </div>
      )}

      {stage === 'request' && (
        <div className="space-y-3">
          <Field label="Nuevo email" help="Enviaremos un código de verificación a este correo.">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className={inputCls}
              placeholder="nuevo@empresa.co"
            />
          </Field>
          <Field
            label="Contraseña actual"
            help="Para confirmar tu identidad."
          >
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className={inputCls}
            />
          </Field>
          {profile?.totp_enabled && (
            <Field
              label="Código TOTP (opcional, requerido si no tienes contraseña)"
              help="Tu authenticator app — 6 dígitos."
            >
              <input
                type="text"
                value={totpCode}
                onChange={(e) =>
                  setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode="numeric"
                maxLength={6}
                className={`${inputCls} font-mono w-32`}
                placeholder="123456"
              />
            </Field>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancel} className={btnSecondaryCls}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => startChange.mutate()}
              disabled={!canStart}
              className={btnPrimaryCls}
            >
              {startChange.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Enviando código…
                </>
              ) : (
                <>Enviar código</>
              )}
            </button>
          </div>
        </div>
      )}

      {stage === 'verify' && (
        <div className="space-y-3">
          <div className="rounded-md border border-info/30 bg-info/10 p-3 text-sm">
            Te enviamos un código a <strong>{maskedTarget}</strong>. Si no llega
            en 5 minutos, revisa spam o cancela y vuelve a empezar.
          </div>
          <Field label="Código de verificación">
            <input
              type="text"
              value={verifyCode}
              onChange={(e) =>
                setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 10))
              }
              inputMode="numeric"
              maxLength={10}
              className={`${inputCls} font-mono text-lg tracking-widest w-40`}
              placeholder="123456"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancel} className={btnSecondaryCls}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => verifyChange.mutate()}
              disabled={verifyCode.length < 4 || verifyChange.isPending}
              className={btnPrimaryCls}
            >
              {verifyChange.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Verificando…
                </>
              ) : (
                <>Confirmar cambio</>
              )}
            </button>
          </div>
        </div>
      )}
    </Section>
  )
}

const inputCls =
  'mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20'
const btnPrimaryCls =
  'inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 px-4 py-2 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:cursor-not-allowed disabled:opacity-50'
const btnSecondaryCls =
  'inline-flex items-center gap-2 rounded-md border border-border bg-bg-app px-4 py-2 text-sm font-medium text-fg-primary hover:bg-bg-elevated'

export { inputCls, btnPrimaryCls, btnSecondaryCls, Field, Section }
